import React, { useState, useRef, useEffect } from 'react';
import { Moon, Sun, Menu, X, ChevronDown } from 'lucide-react';

export const GOAT_ICON_URL =
  'https://res.cloudinary.com/dpymwa41m/image/upload/v1782369990/ChatGPT_Image_Jun_25_2026_09_45_07_AM_xamyqg.png';

const ZEFFY_URL =
  'https://www.zeffy.com/en-US/donation-form/empowering-africas-agricultural-future-helping-african-diary-farmers-thrive';

// The menu, by audience. `page` = page the title links to; `children` = dropdown.
// Order: Farmers, MbuziPlus, Partners, About, Resources.
const MENU = [
  { name: 'Farmers',   page: 'farmers' },
  { name: 'MbuziPlus', page: 'mbuziplus' },
  { name: 'Academy',   page: 'academy' },
  {
    name: 'Partners',
    children: [
      { name: 'Farmer groups',                text: 'Register, train and sell together', page: 'groups' },
      { name: 'Cooperatives',                 text: 'Know every member and their herds', page: 'cooperatives' },
      { name: 'Insurers',                     text: 'Co-underwriting and advisory', page: 'insurers' },
      { name: 'Lenders',                      text: 'Co-lending with verified data', page: 'lenders' },
      { name: 'Veterinary offices',           text: 'Log visits and serve farmers', page: 'vets' },
      { name: 'Buyers and processors',        text: 'Source traceable animals', page: 'buyers' },
      { name: 'Governments and universities', text: 'Data for policy and research', page: 'governments' },
    ],
  },
  {
    name: 'About',
    children: [
      { name: 'About Us',        text: 'Who we are and who we work with', page: 'about' },
      { name: 'What We Do',      text: 'The platform and how it works', page: 'whatwedo' },
      { name: 'Impact',          text: 'Projects and evidence', page: 'impact' },
      { name: 'Make A Donation', text: 'Support the Imfuyo Foundation', href: ZEFFY_URL },
    ],
  },
  {
    name: 'Resources',
    children: [
      { name: 'Apply Here',       text: 'Find a service and apply', page: 'services' },
      { name: 'Blog & Community', text: 'News and stories', page: 'community' },
      { name: 'Market Place',     text: 'Tags, tools, feed and training', page: 'shop' },
    ],
  },
];

const Navbar = ({
  currentPage, onNavigate, isDark, setIsDark,
  mobileMenuOpen, setMobileMenuOpen, activeSection,
}) => {
  const [openKey, setOpenKey] = useState(null);      // desktop dropdown
  const [mobileKey, setMobileKey] = useState(null);  // mobile accordion
  const barRef = useRef(null);

  // Close dropdowns on outside click or Escape
  useEffect(() => {
    const onDown = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpenKey(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenKey(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => { if (!mobileMenuOpen) setMobileKey(null); }, [mobileMenuOpen]);

  const go = (entry) => {
    setOpenKey(null);
    if (mobileMenuOpen) setMobileMenuOpen(false);
    if (entry.href) { window.open(entry.href, '_blank', 'noopener,noreferrer'); return; }
    if (entry.page === 'contact') onNavigate('landing', 'contact');
    else onNavigate(entry.page);
  };

  const isActive = (item) =>
    item.page === currentPage || (item.children || []).some((c) => c.page === currentPage);

  // Hide the bar over the footer section on Home/About (as before)
  if ((currentPage === 'landing' && activeSection === 4) || (currentPage === 'about' && activeSection === 6)) return null;

  const font = { fontFamily: "'Outfit', sans-serif" };
  const text = isDark ? 'text-white' : 'text-gray-900';
  const hover = isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100';
  const navBg = isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200';
  const panel = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';

  return (
    <>
      <nav ref={barRef} className={`w-full backdrop-blur-md border-b shadow-sm ${navBg}`} style={font}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-3">
            {/* Logo */}
            <button className="shrink-0" onClick={() => onNavigate('landing')} aria-label="Imfuyo home">
              <img
                src="https://res.cloudinary.com/dpymwa41m/image/upload/v1761938559/Gemini_Generated_Image_b62mhwb62mhwb62m-removebg-preview_dhwqj1.png"
                alt="Imfuyo"
                className="w-14 h-14 sm:w-16 sm:h-16"
              />
            </button>

            {/* Desktop menu */}
            <ul className="hidden lg:flex items-center justify-evenly flex-1 mx-8">
              {MENU.map((item) => {
                const open = openKey === item.name;
                const active = isActive(item);
                return (
                  <li
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => item.children && setOpenKey(item.name)}
                    onMouseLeave={() => setOpenKey(null)}
                  >
                    <button
                      onClick={() => (item.page ? go(item) : setOpenKey(open ? null : item.name))}
                      aria-haspopup={item.children ? 'true' : undefined}
                      aria-expanded={item.children ? open : undefined}
                      className={`relative flex items-center gap-1 px-2 xl:px-3 py-2 rounded-lg text-[16px] xl:text-[17px] font-medium whitespace-nowrap ${text} ${hover}`}
                    >
                      {item.name}
                      {item.children && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />}
                      {active && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#D55103] rounded-full" />}
                    </button>

                    {item.children && open && (
                      <div className="absolute left-0 top-full pt-2 z-50">
                        <div className={`w-80 rounded-2xl border shadow-xl py-2 ${panel}`}>
                          {item.page && (
                            <button onClick={() => go(item)} className={`w-full text-left px-4 py-2.5 ${hover}`}>
                              <span className={`block font-semibold ${text}`}>{item.name} overview</span>
                              <span className="block text-sm text-gray-500">Start here</span>
                            </button>
                          )}
                          {item.children.map((c) => (
                            <button key={c.name} onClick={() => go(c)} className={`w-full text-left px-4 py-2.5 ${hover}`}>
                              <span className={`block font-semibold ${text}`}>{c.name}</span>
                              <span className="block text-sm text-gray-500">{c.text}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Desktop actions */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsDark && setIsDark(!isDark)}
                aria-label="Toggle dark mode"
                className={`p-2.5 rounded-full ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button
                onClick={() => go({ page: 'contact' })}
                className="px-4 py-2 rounded-full font-semibold text-[15px] bg-[#D55103] hover:bg-[#B84402] text-white"
              >
                Partner with Imfuyo
              </button>
            </div>

            {/* Mobile button */}
            <button
              className={`lg:hidden p-2 rounded-lg ${text} ${hover}`}
              onClick={() => setMobileMenuOpen && setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-[#14301A] text-white overflow-y-auto" style={font}>
          <div className="flex items-center justify-between px-4 h-20">
            <img
              src="https://res.cloudinary.com/dpymwa41m/image/upload/v1761938559/Gemini_Generated_Image_b62mhwb62mhwb62m-removebg-preview_dhwqj1.png"
              alt="Imfuyo" className="w-14 h-14"
            />
            <button onClick={() => setMobileMenuOpen(false)} aria-label="Close menu" className="p-2 rounded-lg bg-white/10">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="px-4 pb-10 max-w-md mx-auto">
            <ul className="divide-y divide-white/10">
              {MENU.map((item) => {
                const open = mobileKey === item.name;
                return (
                  <li key={item.name}>
                    <div className="flex items-center">
                      <button
                        onClick={() => (item.page ? go(item) : setMobileKey(open ? null : item.name))}
                        className="flex-1 text-left py-4 text-xl font-semibold"
                      >
                        {item.name}
                      </button>
                      {item.children && (
                        <button onClick={() => setMobileKey(open ? null : item.name)} aria-label={`Show ${item.name} links`} className="p-3">
                          <ChevronDown className={`w-5 h-5 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {item.children && open && (
                      <ul className="pb-3 pl-3 space-y-1">
                        {item.children.map((c) => (
                          <li key={c.name}>
                            <button onClick={() => go(c)} className="w-full text-left py-2.5 px-3 rounded-lg hover:bg-white/10">
                              <span className="block font-medium">{c.name}</span>
                              <span className="block text-sm text-white/60">{c.text}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 space-y-3">
              <button onClick={() => go({ page: 'contact' })} className="w-full py-3.5 rounded-full bg-[#D55103] font-semibold text-lg">
                Partner with Imfuyo
              </button>
              <button
                onClick={() => setIsDark && setIsDark(!isDark)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-white/10"
              >
                {isDark ? <><Sun className="w-5 h-5" /> Light Mode</> : <><Moon className="w-5 h-5" /> Dark Mode</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
      `}</style>
    </>
  );
};

export default Navbar;
